import React, { useEffect, useState } from 'react';
import InfoRow from 'components/Info/InfoRow';
import { BodyS } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import styles from 'assets/jss/material-dashboard-pro-react/views/contactUs/registeringComplaintStyle';
import { makeStyles } from '@material-ui/core/styles';
import PhoneOutlined from '@material-ui/icons/PhoneOutlined';
import { documentTypeToString } from 'utils/documentTypeToString/documentTypeToString';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { SearchService } from 'services/search/search';
import { SingleSearchNumber } from 'utils/api/models/search/singleSearchNumber';
import { finalize } from 'rxjs/operators';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import { AccountsService } from 'services/accounts/accounts';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';

// @ts-ignore
const useStyles = makeStyles(styles);

interface INumberInfoProps {
	accountNum?: string;
	number: string;
	documentId?: string;
	documentType?: types.organizations.documentType;
	accountType?: string;
	category?: string;
	getNumberDetails?: boolean;
}

const NumberInfo = ({
	accountNum = '',
	accountType = '',
	category = '',
	number,
	documentId = '',
	documentType,
	getNumberDetails = false,
}: INumberInfoProps) => {
	const classes = useStyles();
	const [searchNumber, setSearchNumber] = useState<SingleSearchNumber | undefined>();
	const [corpAccount, setCorpAccount] = useState<SingleCorpAccount | undefined>();
	const [loading, setLoading] = useState<{ searchNumbers: boolean; corpAccounts: boolean }>({
		searchNumbers: false,
		corpAccounts: false,
	});

	useEffect(() => {
		if (getNumberDetails) {
			setLoading((loading) => ({ ...loading, searchNumbers: true }));
			SearchService.searchNumbersAndUsersRestPost(number)
				.pipe(finalize(() => setLoading((loading) => ({ ...loading, searchNumbers: false }))))
				.subscribe(({ numbers }) => {
					if (numbers.length) {
						setSearchNumber(numbers[0]);
					}
				});
		}
	}, [number, getNumberDetails]);

	useEffect(() => {
		if (getNumberDetails && searchNumber) {
			setLoading((loading) => ({ ...loading, corpAccounts: true }));
			AccountsService.getCorpAccountsForCurrentUser()
				.pipe(finalize(() => setLoading((loading) => ({ ...loading, corpAccounts: false }))))
				.subscribe(({ accounts }) => {
					setCorpAccount(accounts.find(({ accountId }) => accountId === searchNumber.accountId));
				});
		}
	}, [searchNumber, getNumberDetails]);

	return (
		<div className={classes.registerTicketNumberInfo}>
			<ConditionalRender
				show={Object.values(loading).some((value) => value)}
				onTrue={() => <CustomCircularProgress text={translate('loading')} loading />}
				onFalse={() => (
					<>
						<InfoRow
							title={
								<BodyS disableMargin color="secondary-light">
									{translate('contact.us.register.ticket.account.number')}
								</BodyS>
							}
							value={
								<BodyS disableMargin color="primary-light">
									{accountNum}
								</BodyS>
							}
							noBorder
						/>
						<ConditionalRender
							show={!!accountType || !!searchNumber?.accountType}
							onTrue={() => (
								<InfoRow
									title={
										<BodyS disableMargin color="secondary-light">
											{translate('contact.us.register.ticket.line.type')}
										</BodyS>
									}
									value={
										<BodyS disableMargin color="primary-light">
											{accountType || searchNumber?.accountType}
										</BodyS>
									}
									noBorder
								/>
							)}
						/>
						<ConditionalRender
							show={!!category}
							onTrue={() => (
								<InfoRow
									title={
										<BodyS disableMargin color="secondary-light">
											{translate('complaints.category')}
										</BodyS>
									}
									value={
										<BodyS disableMargin color="primary-light">
											{category}
										</BodyS>
									}
									noBorder
								/>
							)}
						/>
						<ConditionalRender
							show={!!documentId || !!corpAccount?.documentId}
							onTrue={() => (
								<InfoRow
									title={
										<BodyS disableMargin color="secondary-light">
											{documentTypeToString(
												documentType ||
													corpAccount?.documentType ||
													'COMPANY_REGISTRATION_NUMBER'
											)}
										</BodyS>
									}
									value={
										<BodyS disableMargin color="primary-light">
											{documentId || String(corpAccount?.documentId)}
										</BodyS>
									}
									noBorder
								/>
							)}
						/>
						<div className={classes.divider} />
						<InfoRow
							title={
								<BodyS disableMargin color="secondary-light">
									{translate('contact.us.register.ticket.complaint.number')}
								</BodyS>
							}
							value={
								<div className={classes.contactNumberInfoRow}>
									<PhoneOutlined />
									<BodyS disableMargin color="primary-light">
										{number}
									</BodyS>
								</div>
							}
							noBorder
						/>
					</>
				)}
			/>
		</div>
	);
};

export default NumberInfo;
